import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Send, MapPin, Clock, Smartphone, Mail } from "lucide-react";
import { toast } from "sonner";
import { isPushSupported, subscribeToPush, unsubscribeFromPush, getExistingSubscription } from "@/lib/push";

const guessTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
};

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);
const formatHour = (h) => `${String(h).padStart(2, "0")}:00`;

export default function RemindersCard() {
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [tz, setTz] = useState(guessTz());
  const [hour, setHour] = useState(8);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingPush, setSendingPush] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const pushSupported = isPushSupported();

  useEffect(() => {
    api.get("/preferences").then(async (r) => {
      setEmailEnabled(!!r.data.reminders_enabled);
      setPushEnabled(!!r.data.push_enabled);
      const savedTz = r.data.timezone && r.data.timezone !== "UTC" ? r.data.timezone : guessTz();
      setTz(savedTz);
      if (typeof r.data.reminder_hour === "number") setHour(r.data.reminder_hour);
      // If push_enabled server-side but browser has no sub (new device), reflect reality: keep server value, we'll re-subscribe if user re-toggles
      setLoaded(true);
      if (!r.data.timezone || r.data.timezone === "UTC") {
        api.put("/preferences", {
          reminders_enabled: !!r.data.reminders_enabled,
          timezone: guessTz(),
          reminder_hour: typeof r.data.reminder_hour === "number" ? r.data.reminder_hour : 8,
          push_enabled: !!r.data.push_enabled,
        }).catch(() => {});
      }
    }).catch(() => setLoaded(true));
  }, []);

  const persist = async (patch) => {
    setSaving(true);
    try {
      await api.put("/preferences", {
        reminders_enabled: patch.reminders_enabled ?? emailEnabled,
        timezone: tz,
        reminder_hour: patch.reminder_hour ?? hour,
        push_enabled: patch.push_enabled ?? pushEnabled,
      });
      return true;
    } catch {
      toast.error("No pudimos guardar tus preferencias.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggleEmail = async (value) => {
    setEmailEnabled(value);
    const ok = await persist({ reminders_enabled: value });
    if (ok) toast.success(value ? "Recordatorios por email activados" : "Recordatorios por email desactivados");
  };

  const togglePush = async (value) => {
    if (value) {
      try {
        await subscribeToPush();
        setPushEnabled(true);
        toast.success("Notificaciones push activadas");
      } catch (e) {
        toast.error(e?.message || "No pudimos activar las notificaciones push.");
      }
    } else {
      try {
        await unsubscribeFromPush();
        setPushEnabled(false);
        toast.success("Notificaciones push desactivadas");
      } catch {
        toast.error("No pudimos desactivar las notificaciones push.");
      }
    }
  };

  const changeHour = async (value) => {
    const h = Number(value);
    setHour(h);
    const ok = await persist({ reminder_hour: h });
    if (ok) toast.success(`Hora del recordatorio: ${formatHour(h)}`);
  };

  const sendTestEmail = async () => {
    setSendingEmail(true);
    try {
      const r = await api.post("/reminders/send-test");
      toast.success(`Correo de prueba enviado a ${r.data.sent_to}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "No pudimos enviar el correo de prueba.";
      toast.error(msg);
    } finally {
      setSendingEmail(false);
    }
  };

  const sendTestPush = async () => {
    setSendingPush(true);
    try {
      await api.post("/push/send-test");
      toast.success("Notificación push enviada. Revisa tu navegador.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "No pudimos enviar la notificación push.";
      toast.error(msg);
    } finally {
      setSendingPush(false);
    }
  };

  const anyEnabled = emailEnabled || pushEnabled;

  return (
    <Card className="rounded-2xl border-[var(--ha-border)] bg-white" data-testid="reminders-card">
      <CardContent className="p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-10 w-10 rounded-xl bg-[var(--ha-secondary)]/15 grid place-items-center shrink-0">
            <Bell className="w-5 h-5 text-[var(--ha-secondary)]" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <p className="font-bold text-[var(--ha-text)]">Recordatorio diario</p>
            <p className="mt-1 text-sm text-[var(--ha-muted)] leading-relaxed">Recibe una notificación con la frase del día y tu próxima sesión.</p>
            <p className="mt-2 text-xs text-[var(--ha-muted)] inline-flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={2} /> Hora de {tz}</p>
          </div>
        </div>

        {/* Push toggle */}
        <div className="flex items-center justify-between border-t border-[var(--ha-border)] pt-4">
          <div className="flex items-center gap-3">
            <Smartphone className="w-4 h-4 text-[var(--ha-primary)]" strokeWidth={1.75} />
            <div>
              <p className="text-sm font-semibold text-[var(--ha-text)]">Notificación en el navegador</p>
              <p className="text-xs text-[var(--ha-muted)]">Push nativo · funciona incluso con la pestaña cerrada</p>
              {!pushSupported && <p className="text-xs text-[var(--ha-error)] mt-1">Tu navegador no soporta notificaciones push.</p>}
            </div>
          </div>
          <Switch
            data-testid="push-toggle"
            checked={pushEnabled}
            onCheckedChange={togglePush}
            disabled={saving || !loaded || !pushSupported}
          />
        </div>

        {/* Email toggle */}
        <div className="flex items-center justify-between border-t border-[var(--ha-border)] pt-4 mt-4">
          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-[var(--ha-accent)]" strokeWidth={1.75} />
            <div>
              <p className="text-sm font-semibold text-[var(--ha-text)]">Email diario</p>
              <p className="text-xs text-[var(--ha-muted)]">Se enviará al correo de tu cuenta</p>
            </div>
          </div>
          <Switch
            data-testid="reminders-toggle"
            checked={emailEnabled}
            onCheckedChange={toggleEmail}
            disabled={saving || !loaded}
          />
        </div>

        {anyEnabled && (
          <div className="mt-5 pt-4 border-t border-[var(--ha-border)] flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[var(--ha-muted)]" strokeWidth={1.75} />
              <span className="text-sm text-[var(--ha-muted)]">Hora de envío</span>
              <Select value={String(hour)} onValueChange={changeHour} disabled={saving}>
                <SelectTrigger data-testid="reminder-hour-select" className="h-9 w-[110px] rounded-full border-[var(--ha-border)]">
                  <SelectValue placeholder="08:00" />
                </SelectTrigger>
                <SelectContent>
                  {HOUR_OPTIONS.map(h => (
                    <SelectItem key={h} value={String(h)} data-testid={`reminder-hour-opt-${h}`}>{formatHour(h)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto flex gap-2">
              {pushEnabled && (
                <Button
                  data-testid="send-test-push"
                  variant="outline"
                  size="sm"
                  onClick={sendTestPush}
                  disabled={sendingPush}
                  className="rounded-full border-[var(--ha-border)] h-9 px-4"
                >
                  <Smartphone className="w-3.5 h-3.5 mr-2" strokeWidth={2} /> Probar push
                </Button>
              )}
              {emailEnabled && (
                <Button
                  data-testid="send-test-reminder"
                  variant="outline"
                  size="sm"
                  onClick={sendTestEmail}
                  disabled={sendingEmail}
                  className="rounded-full border-[var(--ha-border)] h-9 px-4"
                >
                  <Send className="w-3.5 h-3.5 mr-2" strokeWidth={2} /> Probar email
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
